import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Input from '../Input/Input';
import Group from '../Group/Group';
import Notice from '../Notice/Notice';

/**
 * `Stopwatch` is a component for displaying time in the format hh:mm:ss:ms. The component acccepts the time value in milliseconds.
 * The components supports arrow key up and down to increase/decrease the values. The component uses the {@link Input} component for each of the input fields.
 * The *value* prop must be updated (with ex state) to update the Stopwatch.
 *
 * @property {node} errorMessage An error Notice that will be shown below the component. Defaults to no message.
 * @property {node} infoMessage An info Notice that will be shown below the component. Defaults to no message.
 * @property {node} successMessage A success 'Notice' that will be shown below the component. Defaults to no message.
 * @property {node} warningMessage A warning "Notice" that will be shown below the component. Defaults to no message.
 * @property {boolean} disabled The component will be disabled if this prop is set to true. Defaults to false.
 * @property {string} label The label of the input field. Defaults to no label.
 * @property {number} value The value in milliseconds
 * @property {number} maxHours Maximum number of hours allowed ex. 12 or 24. If not set there's no limit.
 * @property {string} focused A string to indicate which input field thats should be automatically focused on render. If null no field is focused (default).
 * Possible values are: 'hh', 'mm', 'ss' or 'ms'.
 * @property {function} onChange The change listener that will be called whenever the user changes the input values. The onChange callback will return two parameters. The first one the value in milliseconds and the second an object { hh, mm, ss, ms }
 * @property {function} onClearNotice The callback function that will be called whenever the user has clicked to dismiss the optional Notice.
 * @property {function} onFocus A callback that is called when the component gets the input focus.
 * @property {function} onBlur A callback that will be called when the component has lost focus.
 * @property {boolean} showSeconds Show seconds field. Default true.
 * @property {boolean} showMilliseconds Show milliseconds field. Default true.
 * @property {boolean} showSeperators Show seperators ':' between each input field. Default false.
 * @property {boolean} showSecondaryLabels Show secondary labels above each input field (hrs, min, sec, msec). Default false.
 *
 * @see Input, Notice
 */

export default class Stopwatch extends Component {
  static displayName = 'Stopwatch';

  static propTypes = {
    className: PropTypes.string,
    disabled: PropTypes.bool,
    errorMessage: PropTypes.node,
    errors: PropTypes.object,
    focused: PropTypes.string,
    infoMessage: PropTypes.node,
    label: PropTypes.string,
    maxHours: PropTypes.number,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onClearNotice: PropTypes.func,
    onFocus: PropTypes.func,
    showMilliseconds: PropTypes.bool,
    showSecondaryLabels: PropTypes.bool,
    showSeconds: PropTypes.bool,
    showSeperators: PropTypes.bool,
    successMessage: PropTypes.node,
    value: PropTypes.number,
    warningMessage: PropTypes.node,
  };

  static defaultProps = {
    value: 0,
    showSeconds: true,
    showMilliseconds: true
  };

  constructor(props, context) {
    super(props, context);
    const value = !isNaN(props.value) ? props.value : 0;
    this.state = {
      hh: this.getHours(value),
      mm: this.getMinutes(value),
      ss: this.getSeconds(value),
      ms: this.getMilliseconds(value),
      focused: props.focused
    };
  }

  componentDidMount() {
    switch (this.state.focused) {
      case 'hh': this.hhInput.focus(); break;
      case 'mm': this.mmInput.focus(); break;
      case 'ss': this.ssInput.focus(); break;
      case 'ms': this.msInput.focus(); break;
      default:
    }
  }

  checkPropsUpdate = (prevProps, newProps) => {
    return (
      !(isNaN(prevProps.value) && isNaN(newProps.value)) &&
      (newProps.value !== prevProps.value ||
      (newProps.focused && newProps.focused !== prevProps.focused))
    );
  };

  componentDidUpdate(prevProps) {
    const newProps = this.props;
    if (this.checkPropsUpdate(prevProps, newProps)) {
      const value = !isNaN(newProps.value) ? newProps.value : 0;
      this.setState({
        hh: this.getHours(value),
        mm: this.getMinutes(value),
        ss: this.getSeconds(value),
        ms: this.getMilliseconds(value),
        focused: newProps.focused,
      });
    }
    if (newProps.focused && newProps.focused !== prevProps.focused) {
      switch (this.state.focused) {
        case 'hh': this.hhInput.focus(); break;
        case 'mm': this.mmInput.focus(); break;
        case 'ss': this.ssInput.focus(); break;
        case 'ms': this.msInput.focus(); break;
        default:
      }
    }
  }

  onChangeInput = (type, e) => {
    const { mm, ss, ms } = this.state;
    const value = e.target.value !== '' ? parseFloat(e.target.value) : 0;
    switch (type) {
      case 'hh': this.setHours(value, mm, ss, ms); break;
      case 'mm': this.setMinutes(value); break;
      case 'ss': this.setSeconds(value); break;
      case 'ms': this.setMilliseconds(value); break;
      default:
    }
  };

  onKeyDown = (mode, e) => {
    const { hh, mm, ss, ms } = this.state;
    if (mode === 'hh' && e.keyCode === 38) {
      if (this.props.maxHours > 0 && (hh + 1) <= this.props.maxHours) {
        this.onChange(hh + 1, mm, ss, ms);
      } else if (!this.props.maxHours) {
        this.onChange(hh + 1, mm, ss, ms);
      }
    } else if (mode === 'hh' && e.keyCode === 40 && hh - 1 >= 0) {
      this.onChange(hh - 1, mm, ss, ms);
    } else if (mode === 'mm' && e.keyCode === 38) {
      this.onChange(hh, mm + 1, ss, ms);
    } else if (mode === 'mm' && e.keyCode === 40) {
      if (mm > 0) {
        this.onChange(hh, mm - 1, ss, ms);
      } else if (mm === 0 && hh > 0) {
        this.onChange(hh - 1, 59, ss, ms);
      } else {
        this.onChange(0, 59, ss, ms);
      }
    } else if (mode === 'ss' && e.keyCode === 38) {
      this.onChange(hh, mm, ss + 1, ms);
    } else if (mode === 'ss' && e.keyCode === 40) {
      if (ss > 0) {
        this.onChange(hh, mm, ss - 1, ms);
      } else if (ss === 0 && mm > 0) {
        this.onChange(hh, mm - 1, 59, ms);
      } else {
        this.onChange(hh, 0, 59, ms);
      }
    } else if (mode === 'ms' && e.keyCode === 38) {
      this.onChange(hh, mm, ss, ms + 1);
    } else if (mode === 'ms' && e.keyCode === 40) {
      if (ms > 0) {
        this.onChange(hh, mm, ss, ms - 1);
      } else if (ms === 0 && ss > 0) {
        this.onChange(hh, mm, ss - 1, 999);
      } else {
        this.onChange(hh, mm, 0, 999);
      }
    }
  };

  onBlur = (mode, e) => {
    if (this.props.onBlur) {
      this.props.onBlur(e, mode);
    }
    this.setState({ [mode]: this.formatedValue(mode, this.state[mode]), focused: null });
  }

  onFocus = (mode, e) => {
    if (this.props.onFocus) {
      this.props.onFocus(e, mode);
    }
    this.setState({ focused: mode });
  };

  onChange = (hh, mm, ss, ms) => {
    if (this.props.onChange) {
      this.props.onChange(this.toMilliseconds(hh, mm, ss, ms), { hh, mm, ss, ms });
    }
  };

  getHours = (value) => Math.floor(parseFloat(value) / (1000 * 60.0 * 60));

  getMinutes = (value) => Math.floor((parseFloat(value) / (1000 * 60)) % 60);

  getSeconds = (value) => Math.floor((parseFloat(value) / 1000) % 60);

  getMilliseconds = (value) => Math.floor(parseFloat(value) % 1000);

  setHours = (value) => {
    const { mm, ss, ms } = this.state;
    if (this.props.maxHours && value <= this.props.maxHours && value >= 0) {
      this.onChange(value, mm, ss, ms);
    } else if (!this.props.maxHours) {
      this.onChange(value, mm, ss, ms);
    }
  };

  setMinutes = (value) => {
    const { hh, ss, ms } = this.state;
    if (value < 60 && value >= 0) {
      this.onChange(hh, value, ss, ms);
    }
  };

  setSeconds = (value) => {
    const { hh, mm, ms } = this.state;
    if (value < 60 && value >= 0) {
      this.onChange(hh, mm, value, ms);
    }
  };

  setMilliseconds = (value) => {
    const { hh, mm, ss } = this.state;
    if (value < 1000 && value >= 0) {
      this.onChange(hh, mm, ss, value);
    }
  };

  toMilliseconds = (hh, mm, ss, ms) => (+hh) * 60 * 60 * 1000 + (+mm) * 60 * 1000 + (+ss) * 1000 + (+ms);

  formatedValue = (mode, value) => {
    const valueLength = value?.toString()?.length;
    if (mode === 'ms') {
      if (valueLength === 1) {
        return `00${value}`;
      }
      if (valueLength === 2) {
        return `0${value}`;
      }
    }
    if (valueLength === 1) {
      return `0${value}`;
    }
    return value.toString();
  };

  format = (mode, value) => (this.state.focused === mode ? value : this.formatedValue(mode, value));

  render() {
    const { hh, mm, ss, ms } = this.state;
    const { disabled, errorMessage, errors, infoMessage, label, onClearNotice, showMilliseconds, showSecondaryLabels, showSeconds, showSeperators, successMessage, warningMessage } = this.props;
    const cn = cx('core-stopwatch',
      { 'has-seperators': this.props.showSeperators,
        'has-labels': this.props.showSecondaryLabels,
        'has-errors': errorMessage || errors || warningMessage || infoMessage || successMessage,
      },
      this.props.className
    );

    return (
      <div className={cn}>
        {this.props.label && <label htmlFor='.'>{label}</label>}
        <Group className='stopwatch-wrapper'>
          <Input
            label={showSecondaryLabels && 'hrs'}
            value={this.format('hh', hh)}
            onChange={this.onChangeInput.bind(this, 'hh')}
            onKeyDown={this.onKeyDown.bind(this, 'hh')}
            clearable={false}
            ref={(c) => {
              this.hhInput = c;
            }}
            disabled={disabled}
            onFocus={this.onFocus.bind(this, 'hh')}
            onBlur={this.onBlur.bind(this, 'hh')}
          />
          {showSeperators && (
            <Input
              disabled
              clearable={false}
              value=':'
              label={showSecondaryLabels && '.'}
              className='seperator'
            />
          )}
          <Input
            label={showSecondaryLabels && 'min'}
            value={this.format('mm', mm)}
            onChange={this.onChangeInput.bind(this, 'mm')}
            onKeyDown={this.onKeyDown.bind(this, 'mm')}
            clearable={false}
            ref={(c) => {
              this.mmInput = c;
            }}
            disabled={disabled}
            onFocus={this.onFocus.bind(this, 'mm')}
            onBlur={this.onBlur.bind(this, 'mm')}
          />
          {showSeperators && showSeconds && (
            <Input
              disabled
              clearable={false}
              value=':'
              label={showSecondaryLabels && '.'}
              className='seperator'
            />
          )}
          {showSeconds && (
            <Input
              label={showSecondaryLabels && 'sec'}
              value={this.format('ss', ss)}
              onChange={this.onChangeInput.bind(this, 'ss')}
              onKeyDown={this.onKeyDown.bind(this, 'ss')}
              clearable={false}
              ref={(c) => {
                this.ssInput = c;
              }}
              disabled={disabled}
              onFocus={this.onFocus.bind(this, 'ss')}
              onBlur={this.onBlur.bind(this, 'ss')}
            />
          )}
          {showSeperators && showSeconds && showMilliseconds && (
            <Input
              disabled
              clearable={false}
              value='.'
              label={showSecondaryLabels && '.'}
              className='seperator'
            />
          )}
          {showMilliseconds && showSeconds && (
            <Input
              label={showSecondaryLabels && 'msec'}
              value={this.format('ms', ms)}
              onChange={this.onChangeInput.bind(this, 'ms')}
              onKeyDown={this.onKeyDown.bind(this, 'ms')}
              clearable={false}
              ref={(c) => {
                this.msInput = c;
              }}
              disabled={disabled}
              onFocus={this.onFocus.bind(this, 'ms')}
              onBlur={this.onBlur.bind(this, 'ms')}
            />
          )}
        </Group>
        {(errorMessage || warningMessage || successMessage || infoMessage) && (
          <Notice
            onClear={onClearNotice}
            errorMessage={errorMessage}
            warningMessage={warningMessage}
            successMessage={successMessage}
            infoMessage={infoMessage}
          />
        )}
      </div>
    );
  }
}
